import { Alert } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  useDataConsumer,
  useUpdateDataConsumer,
} from "../../../api/api.data-consumers";
import { useRefreshAll } from "../../../util";
import InlineLoadingCheckbox from "../../InlineLoadingCheckbox";
import { LoadingSkeleton } from "../../LoadingSkeleton";

interface DataConsumerSummaryProps {
  consumerId: string;
  refreshConsumer: boolean;
  getDataConsumerInformation: (info: {
    consumerName: string;
    contactId: string;
    contactName: string;
    contactEmail: string;
  }) => void;
}

export function DataConsumerSummary({
  consumerId,
  refreshConsumer,
  getDataConsumerInformation,
}: DataConsumerSummaryProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [enabled, setEnabled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowPublic, setAllowPublic] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    allowPublic: false,
    enableIntegrations: false,
  });
  const dataConsumer = useDataConsumer(consumerId);
  const refresh = useRefreshAll(dataConsumer);

  const getContactInfo = () => {
    if (dataConsumer.value && dataConsumer.value?.contacts?.length > 0) {
      const contact = dataConsumer.value?.contacts[0];
      return {
        name: contact?.name || "",
        email: contact?.email || "",
        id: contact?.id || "",
      };
    } else {
      return {
        name: "",
        email: "",
        id: "",
      };
    }
  };

  const contactInfo = getContactInfo();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      refresh();
    }
  }, [refreshConsumer]);

  useEffect(() => {
    if (dataConsumer.value) {
      setAllowPublic(dataConsumer.value?.public);
      setEnabled(!dataConsumer.value?.disabled);
      getDataConsumerInformation({
        consumerName: dataConsumer.value?.name,
        contactId: contactInfo.id,
        contactName: contactInfo.name,
        contactEmail: contactInfo.email,
      });
    }
  }, [dataConsumer.value]);

  const updateDataConsumer = useUpdateDataConsumer(consumerId);

  const onCheckBoxChange = async (e: CheckboxChangeEvent) => {
    const { name, checked } = e.target;
    const fieldName = name as keyof typeof loadingStates;

    setLoadingStates((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const updateResult = await updateDataConsumer({
        name: {},
        status: {},
        disabled: name === "enableIntegrations" ? !checked : {},
        public: name === "allowPublic" ? checked : {},
      });

      if (updateResult.response?.data) {
        setAllowPublic(updateResult.response.data.public);
        setEnabled(!updateResult.response.data.disabled);
      }
    } catch (error) {
      setError(t("translations:error-save"));
      console.error("Failed to update data consumer:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  return (
    <div
      className="property-list one"
      style={{ height: "200px" }}
      data-testid="data-consumer-summary"
    >
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <div style={{ display: "flex", gap: "4rem" }}>
        <div style={{ marginRight: "1rem" }}>
          <div className="property-label">
            {t("integrations:data-consumer-name")}
          </div>
          {dataConsumer.loading ? (
            <LoadingSkeleton width={150} loading={dataConsumer.loading} />
          ) : (
            <div className="property-value bottom-margin">
              {dataConsumer.value?.name}
            </div>
          )}
        </div>
        {dataConsumer.value?.organization && (
          <div>
            <div className="property-label">
              {t("translations:organization")}
            </div>
            {dataConsumer.loading ? (
              <LoadingSkeleton width={150} loading={dataConsumer.loading} />
            ) : (
              <a
                className="property-value bottom-margin"
                onClick={() => {
                  history.push(
                    `/organizations/${dataConsumer.value?.organization?.id}`
                  );
                }}
              >
                {dataConsumer.value?.organization?.name}
              </a>
            )}
          </div>
        )}
      </div>
      <div className="property-label">{t("integrations:contact-person")}</div>
      {dataConsumer.loading ? (
        <>
          <LoadingSkeleton
            width={200}
            loading={dataConsumer.loading}
            style={{ marginTop: 0, marginLeft: 12 }}
          />
          <LoadingSkeleton
            width={200}
            loading={dataConsumer.loading}
            style={{ marginTop: 0, marginLeft: 12 }}
          />
        </>
      ) : (
        <div className="vertical-form two-columns bottom-margin">
          <span className="property-label indented"> {t("name")}:</span>
          <span className="property-value"> {contactInfo.name}</span>
          <span className="property-label indented"> {t("user:email")}:</span>
          <span className="property-value">
            <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
          </span>
        </div>
      )}
      {dataConsumer.loading ? (
        <div>
          <LoadingSkeleton width={170} loading={dataConsumer.loading} />
          <LoadingSkeleton width={150} loading={dataConsumer.loading} />
        </div>
      ) : (
        <div>
          <div
            style={{ marginBottom: "10px" }}
            data-testid="allow-public-checkbox"
          >
            <InlineLoadingCheckbox
              name="allowPublic"
              label={t("integrations:allow-public")}
              loading={loadingStates.allowPublic}
              checked={allowPublic}
              disabled={
                dataConsumer.value?.organization === null ? true : false
              }
              onChange={onCheckBoxChange}
            />
          </div>
          <div data-testid="enable-integrations-checkbox">
            <InlineLoadingCheckbox
              name="enableIntegrations"
              label={t("integrations:enable-integrations")}
              loading={loadingStates.enableIntegrations}
              checked={enabled}
              onChange={onCheckBoxChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
